import React, { FunctionComponent, useState, useEffect, Dispatch } from "react";
import { connect, ConnectedProps } from "react-redux";
import { leaveGame } from "../../../redux/actions/GameActions";
import { Card, Button } from "@material-ui/core";
import { database } from "../../../App";
import CenteredLoader from "../../common/CenteredLoader";
import UserIcon from "../../profile/UserIcon";
import LeaveGameButton from "../LeaveGameButton";
import WifiTwoToneIcon from "@material-ui/icons/WifiTwoTone";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import { MINIMUM_PLAYERS_TO_START_GAME } from "../../../constants/game";
import {
  ProfileInterface,
  ProfileInfo
} from "../../../redux/reducers/ProfileReducers";
import {
  GameInterface,
  PlayersType
} from "../../../redux/reducers/GameReducers";
import { AuthInterface } from "../../../redux/reducers/AuthReducers";
import ModalLatestResult from "./ModalLatestResult/ModalLatestResult";

interface actionsType {
  leaveGame: (
    playerID: string,
    gameId: string,
    parentStatus: string
  ) => (
    dispatch: Dispatch<{
      type: string;
    }>
  ) => void;
}
interface RootState {
  profile: ProfileInterface;
  game: GameInterface;
  auth: AuthInterface;
}

const actionsReducers: actionsType = { leaveGame };

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  profile: state.profile,
  game: state.game
});

const connector = connect(mapStateToProps, actionsReducers);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface PlayingGameProps {}
interface SinglePlayerInterface {
  id: string;
  score: number;
  username: string;
  userpic: string;
  isOnline: boolean;
  playerId: string;
  isModerator: boolean;
}
export interface PlayersInterface {
  [playerId: string]: SinglePlayerInterface;
}

const PlayingGame: FunctionComponent<PlayingGameProps & PropsFromRedux> = (
  props
) => {
  const [players, setPlayers] = useState({} as PlayersInterface);
  const [iAmModerator, setIAmModerator] = useState(false);
  const [openModalScores, setOpenModalScores] = useState(false);
  useEffect(() => {
    if (
      props.game.myPlayerId &&
      props.game.info &&
      props.game.info?.latest_results &&
      !props.game.info?.latest_results[props.game.myPlayerId]?.haveSeen
    ) {
      setOpenModalScores(true);
    }
  }, [props.game.info, props.game.myPlayerId]);
  useEffect(() => {
    let isCancelled = false;
    let newPlayer = {};
    if (props.game.info !== undefined && props.auth.user) {
      setIAmModerator(props.auth.user.uid === props.game.info.moderator);
      Object.keys(props.game.info.players).forEach((playerId2: any) => {
        const playerId = playerId2 as PlayersType;
        if (props.game.info) {
          database
            .ref("users")
            .child(props.game.info.players[playerId].id)
            .child("profile")
            .on("value", (snapshot) => {
              const value: ProfileInfo = snapshot.val();
              const { username, userpic, isOnline } = value;
              if (!isCancelled && props.game.info) {
                newPlayer = {
                  ...newPlayer,
                  [playerId]: {
                    ...props.game.info.players[playerId],
                    playerId,
                    username,
                    userpic,
                    isOnline,
                    isModerator:
                      props.game.info.players[playerId].id ===
                      props.game.info.moderator
                  }
                };
                setPlayers(newPlayer);
              }
            });
        }
      });
    }
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.game.info]);

  function renderModal() {
    if (
      !props.game.info ||
      props.game.myPlayerId === undefined ||
      Object.keys(players).length === 0
    ) {
      return <div></div>;
    }
    return (
      <ModalLatestResult
        setOpenModalScores={setOpenModalScores}
        open={openModalScores}
        players={players}
      />
    );
  }

  function renderContent() {
    if (!props.game.info || props.game.myPlayerId === undefined) {
      return <CenteredLoader />;
    }
    switch (props.game.info.status) {
      case "room":
        return (
          <div>
            <h4>{props.game.info.gameName}</h4>
            <div>Playing with {props.game.info.cards.length} cards</div>
            {props.game.info.currentPlayers < props.game.info.maxPlayers ? (
              <div style={{ color: "grey" }}>
                Waiting for players... ({props.game.info.currentPlayers}/
                {props.game.info.maxPlayers})
              </div>
            ) : (
              <p>
                Ready to start ({props.game.info.currentPlayers}/
                {props.game.info.maxPlayers})
              </p>
            )}
            {Object.keys(players).map((player: string, index: number) => (
              <Card
                key={index}
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  padding: "10px",
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                  maxWidth: "400px"
                }}
              >
                <UserIcon
                  width="40"
                  height="40"
                  url={players[player].userpic}
                />
                <div style={{ marginLeft: 10 }}>
                  <div style={{ color: "grey" }}>
                    {players[player].isModerator && "moderator"}
                  </div>
                  <div>
                    {players[player].username}{" "}
                    <span>
                      {players[player].isOnline ? (
                        <WifiTwoToneIcon color="secondary" />
                      ) : (
                        <WifiOffIcon color="error" />
                      )}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
            {iAmModerator &&
              props.game.info.currentPlayers >=
                MINIMUM_PLAYERS_TO_START_GAME && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 10,
                    marginTop: 10
                  }}
                >
                  <Button variant="contained" color="secondary">
                    Start Game
                  </Button>
                </div>
              )}
            <div style={{ textAlign: "center" }}>
              {props.game.myPlayerId && props.game.info && (
                <LeaveGameButton
                  myPlayerId={props.game.myPlayerId}
                  gameId={props.game.info.id}
                  gameName={props.game.info.gameName}
                  text="Leave Room"
                  leaveGame={props.leaveGame}
                />
              )}
            </div>
          </div>
        );
      default:
        return <div>{props.game.info.status}</div>;
    }
  }

  return (
    <div>
      <div>{renderModal()}</div>
      {renderContent()}
    </div>
  );
};

export default connector(PlayingGame);
